import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { isArrayOf, isDefined, isString } from 'lib/typeguards';

import { AdjustmentScheme } from 'components/adjustment-schemes/adjustmentSchemeModel';
import { adjustmentSchemesApi } from 'components/adjustment-schemes/adjustmentSchemesApi';
import { awardEnrichmentApi } from 'components/loyalty/AwardEnrichmentsApi';
import { IAwardEnrichment } from 'components/loyalty/models/AwardEnrichment';
import { isPerk, Perk } from 'components/loyalty/models/PerkModel';
import { perkApi } from 'components/loyalty/PerkApi';
import type { AppDispatch, RootState } from '../store';

export interface MatchCodesState {
    matchCodes: Record<string, string>;
    lastFetchedAt: number | null;
    loading: boolean;
}

export const initialState: MatchCodesState = {
    matchCodes: {},
    lastFetchedAt: null,
    loading: false
};

const matchCodesSlice = createSlice({
    name: 'matchCodes',
    initialState,
    reducers: {
        startRequest(state) {
            state.loading = true;
        },
        loadSuccess(state, action: PayloadAction<Record<string, string> | undefined>) {
            state.loading = false;
            state.matchCodes = action.payload ? action.payload : {};
            state.lastFetchedAt = new Date().getTime();
        },
        loadError(state) {
            state.loading = false;
            state.matchCodes = {};
            state.lastFetchedAt = null;
        },
        resetCache(state) {
            state.matchCodes = {};
            state.lastFetchedAt = null;
        }
    }
});

export const { startRequest, loadSuccess, loadError, resetCache } = matchCodesSlice.actions;

const CACHE_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds

export function getAllMatchCodes() {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        const state = getState();
        const { lastFetchedAt, matchCodes } = state.matchCodes;

        // If we have recent data, return early
        if (lastFetchedAt && matchCodes && Object.keys(matchCodes).length > 0) {
            const now = new Date().getTime();
            if (now - lastFetchedAt < CACHE_DURATION) {
                return;
            }
        }

        dispatch(startRequest());
        try {
            const pepperPerksResponse = await perkApi.getList();
            const awardEnrichmentsResponse = await awardEnrichmentApi.getAllAwardEnrichments();
            const adjustmentSchemesResponse = await adjustmentSchemesApi.getAllSchemes();

            if (!pepperPerksResponse.ok && !awardEnrichmentsResponse.ok && !adjustmentSchemesResponse.ok) {
                throw new Error('Failed to load match codes');
            }

            const perks: Perk[] = pepperPerksResponse.body.perks;
            const awardEnrichments: IAwardEnrichment[] = awardEnrichmentsResponse.items;
            const adjustmentSchemes: AdjustmentScheme[] = adjustmentSchemesResponse.items;

            const result: Record<string, string> = [
                ...perks,
                ...awardEnrichments,
                ...adjustmentSchemes
            ].reduce<Record<string, string>>((acc, perk) => {
                const scopeMatchCode = perk.discount?.scopeMatchCode;
                const matchCode = perk.basketQualification?.matchCode;
                let title = '';
                if (isPerk(perk) && perk.internalTitle && perk.internalTitle.trim() !== '') {
                    title = perk.internalTitle;
                }
                if (isDefined(perk.title) && perk.title.trim() !== '') {
                    title = perk.title;
                }
                if (scopeMatchCode) {
                    if (isArrayOf(isString, scopeMatchCode)) {
                        scopeMatchCode.forEach(code => {
                            acc[code] = `${title} (${code})`;
                        });
                    } else if (isString(scopeMatchCode)) {
                        acc[scopeMatchCode] = `${title} (${scopeMatchCode})`;
                    } else {
                        // Handle grouped scopeMatchCode with the format [[code1, code2], [code3, code4]]
                        scopeMatchCode.forEach(group => {
                            const combinedGroup = group.join(', ');
                            acc[combinedGroup] = `${title} (${combinedGroup})`;
                        });
                    }
                }
                if (matchCode) {
                    if (Array.isArray(matchCode)) {
                        matchCode.forEach(code => {
                            acc[code] = `${title} (${code})`;
                        });
                    } else {
                        acc[matchCode] = `${title} (${matchCode})`;
                    }
                }

                return acc;
            }, {});

            dispatch(loadSuccess(result));
        } catch (_err) {
            dispatch(loadError());
        }
    };
}

export default matchCodesSlice.reducer;
